import React, { useCallback, useEffect, useState } from 'react';
import { SettingsRow } from '@ofself/shell';
import Toggle from '../../components/nucleus/Switch';
import NucleusPage, { Verb } from '../../components/nucleus/NucleusPage';
import { Facts, Note, Tags } from '../../components/nucleus/Inspector';
import DataTable from '../../components/nucleus/DataTable';
import { useToast } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';
import { usePlace } from '../../nucleus/place';
import api from '../../services/api';
import { FormColumn, FormBand } from './selfForm';
import { backTo } from './selfData';
import { ExternalLink } from 'lucide-react';
import { dateOf, plural } from '../../nucleus/format';

const WRITES = new Set(['create', 'write', 'update', 'edit', 'delete', 'purge', 'link']);

// what the public page shows: each item and its kind
const titleOf = (n) => n.title || n.value_json?.title || 'Untitled';
const ON_PAGE_COLUMNS = [
    { id: 'item', label: 'Item', lock: true, min: 180, grow: 2, value: titleOf, cell: titleOf },
    { id: 'kind', label: 'Kind', min: 100, grow: 1, value: (n) => n.schema_name || null, cell: (n) => n.schema_name || '—' },
];

// PUBLIC PAGE — what anyone on ofSelf can see of you (or of the group),
// chosen by one realm on the consent page. Here: whether it is on, what it
// shows, and the doors to change it or take it down.
export default function PublicPage() {
    const { groupId } = usePlace();
    return groupId ? <GroupPublic groupId={groupId} /> : <PersonalPublic />;
}

function PersonalPublic() {
    const { section, page, to, navigate } = usePlace();
    const { user, refreshUser } = useAuth();
    const toast = useToast();
    const [state, setState] = useState(null);
    const [open, setOpen] = useState(!!user?.is_public);
    useEffect(() => { setOpen(!!user?.is_public); }, [user?.is_public]);

    const load = useCallback(async () => {
        const prof = await api.getPublicProfile().catch(() => null);
        const ep = prof?.exposure_profile || null;
        const realm = ep?.privacy_realm_id ? await api.getPrivacyRealm(ep.privacy_realm_id).catch(() => null) : null;
        setState({ ep, realm });
    }, []);
    useEffect(() => { load(); }, [load]);

    const setUp = () => navigate(`/grant?public=true&redirect_uri=${backTo('/people/public')}`);
    const ep = state?.ep;
    const reaches = Object.entries(ep?.verbs || {}).filter(([, v]) => v?.length);

    // the page itself, as the group's Public page offers its own
    return (
        <NucleusPage section={section.label} title={page.label}
                     verbs={ep ? <Verb icon={<ExternalLink />} onClick={() => navigate('/me/public')}>View the page</Verb> : undefined}>
            {state && (
                <FormColumn>
                    <FormBand
                        title="Your public page"
                        aside={ep ? 'On' : 'Off'}
                        lead="What anyone on ofSelf can see of you, chosen through one realm. Nothing is public until you make it so."
                        acts={[
                            { id: 'setup', label: ep ? 'Change what’s shown' : 'Make a public page', primary: true, run: setUp },
                            ep && {
                                id: 'off', label: 'Turn off', danger: true,
                                confirm: { title: 'Turn your public page off?', body: 'Nobody outside your connections sees anything of you any more. You can make it again.', label: 'Turn off' },
                                run: async () => { await api.deletePublicProfile(); toast.success('Your public page is off'); await load(); },
                            },
                        ].filter(Boolean)}
                    >
                        <Facts items={[
                            { label: 'Public page', value: ep ? 'On' : 'Off' },
                            ep && { label: 'Since', value: dateOf(ep.created_at) },
                            ep && { label: 'Through', value: state.realm?.name || 'a realm' },
                            ep && { label: 'Expires', value: ep.expires_at ? dateOf(ep.expires_at) : 'Never' },
                        ]} />
                        {ep && state.realm && (
                            <Tags items={[{ label: `Through ${state.realm.name}`, onPress: () => navigate(to('realms', null, `open=${state.realm.id}`)) }]} />
                        )}
                        {ep && reaches.length === 0 && <Note>It shows nothing yet: the realm reaches no kind of data.</Note>}
                        {reaches.map(([res, verbs]) => (
                            <div key={res} className="nx-reach">
                                <span className="nx-reach__what">{res}</span>
                                <Tags items={verbs.map((v) => ({ label: v, tone: WRITES.has(String(v)) ? 'warn' : undefined }))} />
                            </div>
                        ))}
                    </FormBand>
                    <FormBand title="Follows" lead="Who can follow you to see your public page.">
                        <SettingsRow
                            className="self-setting"
                            label="Open follows"
                            description={open ? 'Anyone can follow you without asking.' : 'You approve each follow.'}
                            control={(
                                <Toggle
                                    checked={open}
                                    aria-label="Open follows"
                                    onChange={async (e) => {
                                        const next = e.target.checked;
                                        setOpen(next);
                                        try {
                                            await api.updateUser(user.id, { is_public: next });
                                            await refreshUser?.();
                                            toast.success(next ? 'Anyone can follow you now' : 'Follows wait for your approval');
                                        } catch (err) {
                                            setOpen(!next);
                                            toast.error(err?.message || 'That didn’t save');
                                        }
                                    }}
                                />
                            )}
                        />
                    </FormBand>
                </FormColumn>
            )}
        </NucleusPage>
    );
}

function GroupPublic({ groupId }) {
    const { section, page, spaceName, navigate, to } = usePlace();
    const toast = useToast();
    const [state, setState] = useState(null);
    // the role from the group itself, as This group and Your share read it: the
    // roster may not be in yet on a cold deep link, and would show an admin locked
    const load = useCallback(async () => {
        const [prof, view, g] = await Promise.all([
            api.getGroupPublicProfile(groupId).catch(() => null),
            api.getGroupPublicView(groupId).catch(() => null),
            api.getGroup(groupId).catch(() => null),
        ]);
        setState({
            on: prof ? !!prof.exposure_profile : !!view?.is_public,
            ep: prof?.exposure_profile || null,
            nodes: view?.nodes || [],
            admin: g?.my_role === 'admin',
        });
    }, [groupId]);
    useEffect(() => { setState(null); load(); }, [load]);
    const isAdmin = !!state?.admin;
    const realm = state?.ep?.privacy_realm || null;
    const lock = isAdmin ? {} : { disabled: true, hint: 'Only a group admin can change this' };
    const view = () => navigate(`/group/${groupId}/public`);

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            verbs={state?.on ? <Verb icon={<ExternalLink />} onClick={view}>View the page</Verb> : undefined}
        >
            {state && (
                <FormColumn>
                    <FormBand
                        title={`${spaceName}’s public page`}
                        aside={state.on ? 'On' : 'Off'}
                        lead="What anyone on ofSelf can see of the group, chosen through one of its realms."
                        acts={[
                            {
                                id: 'setup', label: state.on ? 'Change what’s public' : 'Make a public page', primary: true, ...lock,
                                run: () => navigate(`/grant?public=true&group_id=${groupId}&redirect_uri=${backTo(`/group/${groupId}/self/public`)}`),
                            },
                            state.on && {
                                id: 'down', label: 'Take it down', danger: true, ...lock,
                                confirm: { title: `Take ${spaceName}’s public page down?`, body: 'Nobody outside the group can see it any more. You can publish it again.', label: 'Take it down' },
                                run: async () => { await api.deleteGroupPublicProfile(groupId); toast.success(`${spaceName}’s public page is down`); await load(); },
                            },
                        ].filter(Boolean)}
                    >
                        <Facts items={[
                            { label: 'Public page', value: state.on ? 'On' : 'Off' },
                            state.ep && { label: 'Since', value: dateOf(state.ep.created_at) },
                            state.ep && { label: 'Through', value: realm?.name || 'a realm' },
                            state.ep && { label: 'Expires', value: state.ep.expires_at ? dateOf(state.ep.expires_at) : 'Never' },
                            state.on && { label: 'Shows', value: plural(state.nodes.length, 'thing') },
                        ]} />
                        {state.ep && realm && (
                            <Tags items={[{ label: `Through ${realm.name}`, onPress: () => navigate(to('realms', null, `open=${realm.id}`)) }]} />
                        )}
                        {!isAdmin && <Note>Only a group admin can change this.</Note>}
                    </FormBand>
                    {state.on && (
                        <FormBand title="On the page" aside={state.nodes.length} >
                            <DataTable
                                id="self-public-nodes" label="On the page" columns={ON_PAGE_COLUMNS} rows={state.nodes}
                                keyOf={(n) => n.id ?? state.nodes.indexOf(n)} empty="Nothing yet."
                            />
                        </FormBand>
                    )}
                </FormColumn>
            )}
        </NucleusPage>
    );
}
